/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import CommunicatorNumber from '../number/communicator-number';

import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isNil from 'lodash/isNil';
import isNull from 'lodash/isNull';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import merge from 'lodash/merge';

/**
 * Utility komunikátoru pro Ki-Wi Server.
 */
class CommunicatorUtility {
  /**
   * Zpracuje samostatný čas bez datumu.
   * @param {string} [inputString] Čas ve formátu, který vrací server.
   * @returns {Object} Objekt s hodnotami v `Date#getHours`, `Date#getMinutes` a `Date#getSeconds` formátu.
   */
  static parseClock(inputString) {
    // Výstupní hodnoty s pomocnými daty seřazené od nejnižšího řádu.
    const resultArray = [
      { key: 'second', max: 60 },
      { key: 'minute', max: 60 },
      { key: 'hour', max: 24 }
    ];
    const { length } = resultArray;
    if (isString(inputString)) {
      // Načtení hodnot oddělených dvojtečkou.
      const inputArray = inputString.split(':', length);
      // Omezení načtených hodnot dle počtu vstupů.
      inputArray.length = length;
      // Seřazení načtených hodnot od nejnižšího řádu.
      inputArray.reverse();
      // Výpočet jednotlivých výstupů.
      resultArray.forEach((result, index, array) => {
        let overflow;
        let value;
        // Načtení hodnoty aktuálního řádu.
        const inputNumber = CommunicatorNumber.fromString(inputArray[index], {
          nil: null
        });
        if (isNull(inputNumber) || !Number.isFinite(inputNumber)) {
          overflow = null;
          value = null;
        } else {
          // Přetečení z nižšího řádu.
          const carry = get(array[index - 1], 'overflow', 0);
          // Hodnota aktuálního řádu s přetečením.
          const total = inputNumber + carry;
          overflow = Math.max(0, total - result.max + 1);
          value = total % result.max;
        }
        // Přetečení do vyššího řádu.
        result.overflow = overflow;
        // Výsledná hodnota aktuálního řádu.
        result.value = value;
      });
    } else {
      // Výchozí hodnoty.
      resultArray.forEach((result) => {
        result.value = null;
      });
    }
    // Spojení pole výstupních hodnot do objektu.
    return resultArray.reduce((acc, result) => {
      acc[result.key] = result.value;
      return acc;
    }, {});
  }

  /**
   * Načte pravdivostní hodnotu.
   * @param {string} [value] Příznak ve formátu, který vrací server.
   * @returns {boolean|null} Příznak jako `boolean`.
   */
  static parseBoolean(value) {
    if (isNil(value)) {
      return null;
    }
    if (!isString(value)) {
      throw new TypeError(`Invalid boolean "${value}".`);
    }
    return value === 'true';
  }

  /**
   * Zpracuje datum.
   * @param {number|string} [value] Datum ve formátu, který vrací server.
   * @returns {string|null} Datum v ISO formátu.
   */
  static parseDate(value) {
    let result;
    if (isString(value) || isNumber(value)) {
      const date = new Date(value);
      if (isNaN(date)) {
        result = null;
      } else {
        result = date.toISOString();
      }
    } else {
      result = null;
    }
    return result;
  }

  /**
   * Zpracuje den v týdnu.
   * @param {number} value Číslo dne ve formátu, který vrací server.
   * @returns {number} Datum v `Date#getDay` formátu (0 = neděle).
   */
  static parseDay(value) {
    const number = CommunicatorUtility.parseNumber(value);
    if (isNull(number) || !Number.isFinite(number)) {
      return null;
    }
    return (value + 1) % 7;
  }

  // TODO #151 Zrušit v rámci opuštění rozhraní DsMap.
  /**
   * Zpracuje seznam modelů včetně zanořených.
   * @param {Array.<Object>} jsonArray Část odpovědi na obsah zařízení se seznamem modelů.
   * @param {Function} iterator Callback funkce pro zpracování jedné položky modelu.
   * @param {DsMap} initial Prázdná mapa datových atributů.
   * @returns {DsMap} Mapa datových atributů ke vložení do datového skladu.
   */
  static parseMany(jsonArray, iterator, initial) {
    let result;
    if (isArray(jsonArray)) {
      result = jsonArray.reduce(
        (accumulator, ...args) => merge(accumulator, iterator(...args)),
        initial
      );
    } else {
      result = initial;
    }
    return result;
  }

  // TODO Samostatné metody pro vstupy `number` a `string`.
  // - Zrušit po odstranění posledních závislostí.
  /**
   * Zpracuje číslo.
   * @deprecated Používat CommunicatorNumber dle vstupního typu.
   * @param {number|string} [value] Číslo ve formátu, který vrací server.
   * @returns {null|number} Konečné číslo.
   */
  static parseNumber(value) {
    if (isNil(value)) {
      return null;
    } else if (isNumber(value)) {
      return CommunicatorNumber.fromNumber(value, { nil: null });
    } else if (isString(value)) {
      return CommunicatorNumber.fromString(value, { nil: null });
    }
    throw new TypeError(`Invalid value "${value}".`);
  }

  /**
   * Zpracuje odkaz na jiný objekt.
   * @param {Object} [json] Část odpovědi na obsah zařízení s jedním odkazem.
   * @param {string} key Klíč relace, která se má načíst.
   * @param {Object} [opts={}]
   * @param {null|undefined} [opts.nil] Hodnota pro prázdnou referenci,
   * která slouží především k postupné migraci z `undefined` na `null`.
   * @returns {DsId|null|undefined} Identifikátor na který se odkazuje.
   */
  static parseReference(json, key, { nil } = {}) {
    if (!isObject(json) || json.type !== key) {
      return nil;
    }
    const { id } = json;
    if (!isString(id) && !Number.isFinite(id)) {
      return nil;
    }
    return id;
  }

  /**
   * Načte řetězec.
   * @param {string} [value] Řetězec ve formátu, který vrací server.
   * @param {Object} [opts={}]
   * @param {null|undefined} [opts.nil] Hodnota pro prázdnou referenci,
   * která slouží především k postupné migraci z `undefined` na `null`.
   * @param {boolean} [opts.required] Při nastavení na `true` vrací vždy
   * `string`, nebo vyhodí chybu.
   * @returns {null|string|undefined} Řetězec jako `string`.
   */
  static parseString(value, { nil, required = false } = {}) {
    if (isNil(value)) {
      if (required) {
        throw new TypeError(`Missing string "${value}".`);
      }
      return nil;
    }
    if (!isString(value)) {
      throw new TypeError(`Invalid string "${value}".`);
    }
    return value;
  }

  // TODO Samostatné metody pro vstupy `number` a `string`.
  // - Přesunou do samostatného modulu a rozděli na 2 metody.
  // - Zvážit jestli nepovolit výstup NaN apod.
  /**
   * Zpracuje časový údaj.
   * @param {number|string} [value] Čas ve formátu, který vrací server.
   * @returns {null|number} Počet ms.
   */
  static parseTime(value) {
    const number = CommunicatorUtility.parseNumber(value);
    if (isNull(number) || !Number.isFinite(number)) {
      return null;
    }
    return Math.round(number * 1000);
  }

  // TODO Samostatné metody pro vstupy `number` a `string`.
  // - Přesunou do samostatného modulu a rozděli na 2 metody.
  // - Zvážit jestli nepovolit výstup NaN apod.
  /**
   * Zpracuje hlasitost.
   * @param {number|string} [value] Hlasitost ve formátu, který vrací server.
   * @returns {null|number} Hlasitost na škále od 0.0 (potichu) do 1.0 (nahlas).
   */
  static parseVolume(value) {
    const number = CommunicatorUtility.parseNumber(value);
    if (isNull(number) || !Number.isFinite(number)) {
      return null;
    }
    return number / 100;
  }
}

export default CommunicatorUtility;
